import assert from 'assert';
import type { Table } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { isNil } from 'es-toolkit';

import type { ExportableColumn, ExportConfig, ExportFeatureConfig, ExportFormat, ExportState } from '../types/export';
import { executeClientExport, handleExportResult } from '../utils/export/client-export';
import { getDefaultFilename } from '../utils/export/filename';
import { getExportableColumns } from '../utils/export/table-export';

export type UseExportOptions<TData> = {
  readonly table: Table<TData>;
  readonly exportConfig: ExportFeatureConfig | null;
  readonly tableName?: string;
};

export type UseExportReturn = {
  readonly selectedColumnIds: string[];
  readonly selectedFormat: ExportFormat;
  readonly state: ExportState;
  readonly availableColumns: ExportableColumn[];
  readonly selectedColumns: ExportableColumn[];
  readonly canExport: boolean;
  readonly setSelectedFormat: (format: ExportFormat) => void;
  readonly toggleColumn: (columnId: string) => void;
  readonly addColumn: (columnId: string) => void;
  readonly removeColumn: (columnId: string) => void;
  readonly reset: () => void;
  readonly execute: () => Promise<void>;
  readonly modalOpen: boolean;
  readonly setModalOpen: (open: boolean) => void;
  readonly handleModalOpenChange: (open: boolean) => void;
  readonly exportEnabled: boolean;
  readonly formats: ExportFormat[];
  readonly columns: ExportableColumn[];
};

export function useExport<TData>(options: UseExportOptions<TData>): UseExportReturn {
  const { table, exportConfig, tableName } = options;

  const { columns, formats, onExport } = useMemo(() => {
    if (!exportConfig) {
      return {
        columns: [],
        formats: ['csv' as const],
        onExport: async () => {
          // No-op for disabled export
        },
      };
    }

    const exportableColumns = getExportableColumns(table);
    const exportFormats = exportConfig.formats ?? ['csv' as const];
    const { filename: configFilename, onExport: configOnExport } = exportConfig;

    function resolveExportFilename(format: ExportFormat): string {
      if (typeof configFilename === 'function') {
        return configFilename(format);
      }
      if (!isNil(configFilename) && configFilename !== '') {
        return configFilename;
      }
      return getDefaultFilename(tableName, format);
    }

    async function handleExport(
      partialConfig: Omit<ExportConfig, 'filters' | 'sorting' | 'globalFilter'>,
    ): Promise<void> {
      const fullConfig: ExportConfig = {
        ...partialConfig,
        filters: table.getState().columnFilters,
        sorting: table.getState().sorting,
        globalFilter: table.getState().globalFilter as string | undefined,
      };

      if (configOnExport) {
        const result = await configOnExport(fullConfig);
        handleExportResult(result);
      } else {
        await executeClientExport({
          table,
          columnIds: partialConfig.columnIds,
          format: partialConfig.format,
          filename: resolveExportFilename(partialConfig.format),
        });
      }
    }

    return {
      columns: exportableColumns,
      formats: exportFormats,
      onExport: handleExport,
    };
  }, [table, exportConfig, tableName]);

  const defaultFormat = useMemo(() => {
    const defaultFormat = formats[0];
    assert(defaultFormat, 'formats must be an array with at least one format');
    return defaultFormat;
  }, [formats]);

  const defaultColumnIds = useMemo(() => columns.filter((col) => col.isVisible).map((col) => col.id), [columns]);

  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>(defaultColumnIds);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(defaultFormat);
  const [state, setState] = useState<ExportState>({ status: 'idle', error: null });
  const [modalOpen, setModalOpen] = useState(false);

  const exportEnabled = exportConfig !== null;

  const availableColumns = useMemo(
    () => columns.filter((col) => !selectedColumnIds.includes(col.id)),
    [columns, selectedColumnIds],
  );

  const selectedColumns = useMemo(
    () =>
      selectedColumnIds
        .map((id) => columns.find((col) => col.id === id))
        .filter((col): col is ExportableColumn => col !== undefined),
    [columns, selectedColumnIds],
  );

  const canExport = selectedColumnIds.length > 0 && state.status !== 'exporting';

  const toggleColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  }, []);

  const addColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
  }, []);

  const removeColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => prev.filter((id) => id !== columnId));
  }, []);

  const reset = useCallback(() => {
    setSelectedColumnIds(defaultColumnIds);
    setSelectedFormat(defaultFormat);
    setState({ status: 'idle', error: null });
  }, [defaultColumnIds, defaultFormat]);

  const handleModalOpenChange = useCallback(
    (open: boolean) => {
      setModalOpen(open);
      if (open) {
        reset();
      }
    },
    [reset],
  );

  const execute = useCallback(async () => {
    setState((prevState) => {
      if (prevState.status === 'exporting' || selectedColumnIds.length === 0) {
        return prevState;
      }

      return { status: 'exporting', error: null };
    });

    try {
      await onExport({
        columnIds: selectedColumnIds,
        format: selectedFormat,
      });
      setState({ status: 'success', error: null });
    } catch (err) {
      setState({
        status: 'error',
        error: err instanceof Error ? err.message : 'Export failed. Please try again.',
      });
      throw err;
    }
  }, [onExport, selectedColumnIds, selectedFormat]);

  return {
    selectedColumnIds,
    selectedFormat,
    state,
    availableColumns,
    selectedColumns,
    canExport,
    setSelectedFormat,
    toggleColumn,
    addColumn,
    removeColumn,
    reset,
    execute,
    modalOpen,
    setModalOpen,
    handleModalOpenChange,
    exportEnabled,
    formats,
    columns,
  };
}
